import getColumnSearchProps, {
  DataIndex,
} from '../../components/table/getColumnSearchProps'
import { useIntl } from 'react-intl'
import { Category } from '../../models/Category'
import { useEffect, useRef } from 'react'
import PageTitle from '../../components/utils/PageTitle'
import { Breadcrumb, Card, InputRef, Table } from 'antd'
import { Actions } from '../../modules/quick-add/Actions'
import { QuickAddType } from '../../modules/quick-add/Models'
import { extractSortKeys } from '../../utils/helpers/TableHelper'
import { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import { useAppDispatch, useAppSelector } from '../../utils/hooks'
import {
  getCategories,
  selectCategories,
  setCategorieQuery,
} from '../../reducers/CategoryReducer'

const Categories = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const searchInput = useRef<InputRef>(null)

  const categories = useAppSelector(selectCategories())
  const { query, isLoading } = useAppSelector((state) => state.category)
  const { isOpen: quickAddIsOpen } = useAppSelector((state) => state.quickadd)

  const handleSearch = (
    selectedKeys: string[],
    dataIndex: DataIndex<Category>
  ) => {
    const newQuery = {
      ...query,
      ...{ search: { [dataIndex]: selectedKeys[0] } },
    }
    dispatch(setCategorieQuery(newQuery))
    dispatch(getCategories(newQuery))
  }

  const handleSearchReset = (clearFilters: () => void) => {
    clearFilters()
    const newQuery = {
      ...query,
      ...{ search: { dataIndex: null } },
    }
    dispatch(setCategorieQuery(newQuery))
    dispatch(getCategories(newQuery))
  }

  const columns: ColumnsType<Category> = [
    {
      fixed: 'left',
      key: 'name',
      title: intl.formatMessage({ id: 'ADMIN.TABLE.COLUMNS.NAME' }),
      sorter: true,
      width: 250,
      ...getColumnSearchProps<Category>(
        'name' as keyof Category,
        searchInput,
        handleSearch,
        handleSearchReset,
        intl
      ),
      render: (record: Category) => (
        <>
          🇫🇷 {record.fr.name}
          <br />
          🇬🇧 {record.en.name}
        </>
      ),
    },
    {
      key: 'actions',
      align: 'right',
      render: (record: Category) => (
        <Actions
          actions={['update', 'delete']}
          entity={QuickAddType.category}
          entityId={record.id}
        />
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Category> | SorterResult<Category>[]
  ) => {
    const newQuery = {
      ...query,
      pagination,
      filters: { ...query.filters, ...filters },
      ...extractSortKeys(sorter),
    }
    dispatch(setCategorieQuery(newQuery))
    dispatch(getCategories(newQuery))
  }

  useEffect(() => {
    if (quickAddIsOpen) {
      return
    }
    // Trigger when QuickAdd is closed
    dispatch(getCategories(query))
  }, [dispatch, quickAddIsOpen])
  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: 'Home' }, { title: 'Categories' }]}
      />
      <Card
        bordered={false}
        bodyStyle={{ padding: 0 }}
        title={
          <PageTitle
            title="Categories"
            level={5}
            badge={categories.length}
            toolbar={
              <Actions actions={['create']} entity={QuickAddType.category} />
            }
          />
        }
      >
        <Table
          scroll={{ x: 600 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={categories}
          pagination={query.pagination}
          loading={isLoading && !categories.length}
          onChange={handleTableChange}
        />
      </Card>
    </>
  )
}

export default Categories
